import { Box, Grid, IconButton, Typography } from '@mui/material';
import React, { MutableRefObject, useRef, useState } from 'react';
import PlayerMessage from './PlayerMessage';
import TranslationHelper from '../../../helpers/TranslationHelper';
import VideoJSPlayer from '../../../components/VideoJSPlayer';
import Player from 'video.js/dist/types/player';
import useRetryOnError from '../../../hooks/useRetryOnError';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PLAYBACK_RATES } from '../../../constants/videoPlayerSettings';

interface IOwnProps {
    cameraName: string;
    smallerSize: boolean;
    videoSource: string;
}

const LiveViewPlayer = ({
    cameraName,
    smallerSize,
    videoSource,
}: IOwnProps) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = React.useRef<Player | null>(null);
    const [vertical, setVertical] = useState(false);
    const [errors, setError] = useState(false);
    const handleRetryOnError = useRetryOnError();

    const videoJsOptions = {
        autoplay: 'muted',
        playbackRates: PLAYBACK_RATES,
        controlBar: {
            liveDisplay: true,
            seekToLive: true,
        },
        plugins: {
            hotkeys: {
                seekStep: 10,
                enableModifiersForNumbers: false,
            },
        },
        controls: true,
        fluid: true,
        sources: [
            {
                src: videoSource,
                type: 'application/x-mpegURL',
            },
        ],
    };

    const handleOrientation = (vertical: boolean) => {
        setVertical(vertical);
    };

    const handleError = () => {
        setError(true);
    };

    const resetStream = () => {
        setError(false);
    };

    const handleRetryOnSrcError = (
        playerRef: MutableRefObject<Player | null>
    ) => {
        const player = playerRef.current;
        handleRetryOnError({
            preRetryAction: () => {
                //must be null here to hide error dialog
                //@ts-ignore
                player.error(null);
            },
            retryAction: () => {
                if (player && !player.isDisposed()) {
                    player.pause();
                    player.load();
                }
            },
            finalAction: () => {
                handleError();
            },
            maxRetries: 15,
        });
    };

    return (
        <Grid item xs={vertical ? 6 : 12} lg={smallerSize && vertical ? 3 : 6}>
            <Typography>{TranslationHelper.translate(cameraName)}</Typography>
            {!errors ? (
                <Box>
                    <VideoJSPlayer
                        options={videoJsOptions}
                        videoRef={videoRef}
                        playerRef={playerRef}
                        handleError={() => handleError()}
                        handleSrcError={() => handleRetryOnSrcError(playerRef)}
                        handleOrientation={(vertical) =>
                            handleOrientation(vertical)
                        }
                    />
                </Box>
            ) : (
                <PlayerMessage message="The stream cannot be played">
                    <IconButton onClick={() => resetStream()}>
                        <RefreshIcon />
                    </IconButton>
                </PlayerMessage>
            )}
        </Grid>
    );
};

export default LiveViewPlayer;
